import { getIntl } from '../../../../../../utils/intl';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { Action } from '../../../../../v2BaseComponents/ActionButtonLayout/types';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import useMedicalProfileFiles from '../../../../../../hooks/api/useMedicalProfileFiles';
import Text from '../../../../../v2BaseComponents/Text';

const intl = getIntl('DeleteFolderButton', {
    promptHeader: 'Delete Folder',
    emptyFolderContent: 'This will delete this empty folder.  This cannot be undone',
    folderNotEmptyContent:
        'This folder is not empty.  Are you sure you want to delete it?  This will also delete the {fileCount} file{plural} in this folder.',
    delete: 'Delete Folder',
    cancel: 'Cancel',
});

export type UseDeleteFolderActionArgs = {
    folderId: string;
    medicalProfileId: string;
    onSetFolderId: (folderId: string) => void;
};

export type UseDeleteFolderActionReturnValue = {
    action: Action | null;
    Prompt: React.ReactNode;
};

export const useDeleteFolderAction = (args: UseDeleteFolderActionArgs): UseDeleteFolderActionReturnValue => {
    const { folderMetadata } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        folderId: args.folderId,
    });
    const { deleteFolder, isDeletingFolder } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        folderId: args.folderId,
        onDeletedFolderSuccess: () => {
            if (folderMetadata?.targetFolder.isRoot === false) {
                const parentFolder =
                    folderMetadata.targetFolder.folder.fullFolderPath[
                        folderMetadata.targetFolder.folder.fullFolderPath.length - 2
                    ];
                args.onSetFolderId(parentFolder?.folderId || 'root');
            }
        },
    });
    const promptButton = usePromptButton({
        promptTitle: intl('promptHeader'),
        renderPromptContents: (onClose) =>
            folderMetadata?.targetFolder.isRoot === false ? (
                <ContentStack>
                    <Text variant="body-1">
                        {folderMetadata?.targetFolder.folder.descendantFilesCount === 0
                            ? intl('emptyFolderContent')
                            : intl('folderNotEmptyContent', {
                                  fileCount: folderMetadata?.targetFolder.folder.descendantFilesCount,
                                  plural: folderMetadata?.targetFolder.folder.descendantFilesCount ? '' : 's',
                              })}
                    </Text>
                    <PromptActions
                        actions={[
                            {
                                title: intl('delete'),

                                isDisabled: isDeletingFolder,
                                onPress: () => {
                                    deleteFolder(args.folderId);
                                    onClose();
                                },
                                testID: 'delete-folder-confirm-button',
                                icon: 'FolderMinus',
                                variant: 'alert',
                            },
                        ]}
                    />
                </ContentStack>
            ) : null,
    });

    if (folderMetadata?.targetFolder.isRoot) return { action: null, Prompt: null };

    return {
        action: {
            title: intl('promptHeader'),
            icon: 'FolderMinus',
            onPress: promptButton.onPress,
            show: true,
            testID: 'delete-folder-button',
        },
        Prompt: promptButton.Prompt,
    };
};
